import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { Table, Input, Button, Select, AutoComplete, Pagination } from 'antd'
import Layout from '@components/Layout/index.jsx'
import ViewProductionWorkersModal from '@components/Modal/viewProductionWorkerModal.jsx'
import SuccessModalWithTimer from '@components/Modal/SuccessModalWithTimer/index.jsx'
import ErrorModalWithTimer from '@components/Modal/ErrorModalWithTimer/index.jsx'
import { columnsPerfectCut } from '@utils/columns.jsx'
import moment from 'moment'
import { http } from '@services/http.js'
import { useSelector } from 'react-redux'
import _ from 'lodash'
import './table.css'
import { debouncedSet } from '@utils/debounce.js'
import { handleMarshrutWorkers } from '@utils/navigateToPdf.jsx'
import {
	calculateDurationString,
	isAfter,
	isBefore,
	isSameDay,
} from '@utils/time.js'
import {
	switchBgColor,
	meaningOfBg,
	ignoredKeys,
} from '@utils/switchBgProductionsTables.js'
import { FileDown } from 'lucide-react'
import { changePagination, changePageSize } from '@utils/pagination'

const fetchSalesOrder = async ({ queryKey }) => {
	const [
		_,
		page,
		cardName,
		docNum,
		slpName,
		cancelStatus,
		delStatus,
		pageSize,
		department,
		absoluteStatus,
	] = queryKey
	try {
		let url = `api/sales/sotuv-buyurtmasi?cardName=${cardName}&docNum=${docNum}&slpName=${slpName}&delStatus=${delStatus}&department=${department}&absoluteStatus=${absoluteStatus}&inventoryStatus=O&isCancelRequested=${cancelStatus}&skip=${page}&pageSize=${pageSize}`
		const { data } = await http.get(url)
		const formattedData = data.data
		return formattedData
	} catch (error) {
		console.log(error)
	}
}

const ExportPerfectCut = () => {
	const { t } = useTranslation()
	const formattedDate = moment()
	const { roleNames, getMe } = useSelector((state) => state.main)
	const successRef = useRef()
	const errorRef = useRef()

	const [currentPage, setCurrentPage] = useState(0)
	const [isModalVisible, setIsModalVisible] = useState(false)
	const [selectedRowData, setSelectedRowData] = useState(null)
	const [docNum, setDocNum] = useState('')
	const [startDate, setStartDate] = useState('')
	const [endDate, setEndDate] = useState(
		moment().endOf('month').format('YYYY-MM-DD'),
	)
	const [selectedRowKeys, setSelectedRowKeys] = useState([])
	const [selectedRowDocNums, setSelectedRowDocNums] = useState([])
	const [selectedRows, setSelectedRows] = useState([])
	const [btnLoading, setBtnLoading] = useState(false)
	const [excelBtnLoading, setExcelBtnLoading] = useState(false)
	const [txtBtnLoading, setTxtBtnLoading] = useState(false)
	const [cardName, setCardName] = useState('')
	const [slpName, setSlpName] = useState('')
	const [cancelStatus, setCancelStatus] = useState('-')
	const [delStatus, setDelStatus] = useState('')
	const [pageSize, setPageSize] = useState('50')
	const [absoluteStatus, setAbsoluteStatus] = useState('')

	const {
		data = [],
		isLoading,
		refetch,
	} = useQuery({
		queryKey: [
			'perfectCut',
			currentPage,
			cardName,
			docNum,
			slpName,
			cancelStatus,
			delStatus,
			pageSize,
			roleNames.includes('Supplier') || roleNames.includes('admin')
				? ''
				: department,
			absoluteStatus,
		],
		queryFn: fetchSalesOrder,
		keepPreviousData: true,
		refetchOnWindowFocus: false,
	})

	const handleNextPage = () => {
		if (data.length === +pageSize) {
			setCurrentPage((prevPage) => prevPage + 1)
		}
	}

	const handlePreviousPage = () => {
		if (currentPage > 0) {
			setCurrentPage((prevPage) => prevPage - 1)
		}
	}

	const handleRowClick = (record) => {
		setSelectedRowData(record)
		setIsModalVisible(true)
	}

	const handleStartDate = (e) => {
		setStartDate(e.target.value)
	}

	const handleEndDate = (e) => {
		setEndDate(e.target.value)
	}

	const handleDocNumChange = (val) => {
		debouncedSet(val, setDocNum)
	}
	const handleCardNameChange = (val) => {
		debouncedSet(val, setCardName)
	}
	const handleSlpNameChange = (val) => {
		debouncedSet(val, setSlpName)
	}

	const onSelectChange = (selectedKeys, rows) => {
		setSelectedRowKeys(selectedKeys)
		setSelectedRowDocNums(rows.map((item) => item.docNumPrefix))
		setSelectedRows(rows)
	}

	const columns = columnsPerfectCut(
		t,
		handleRowClick,
		handleDocNumChange,
		handleCardNameChange,
		handleSlpNameChange,
	)

	const handleItemSearch = (value) => {
		debouncedSet(value, setSearchItemName)
	}

	const handleExportExcel = async (selectedData) => {
		if (!selectedData?.length) {
			message.warning('Please select some data to export.')
			return
		}

		setExcelBtnLoading(true)
		const docEntries = selectedData.map((item) => item.docEntry).join(',')
		const body = {
			docEntries,
			startDate,
			endDate,
		}
		try {
			const res = await http.post(
				`api/perfectcut/export-to-excel`,
				body,
				{
					// headers: {
					// 	'Content-Type':
					// 		'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
					// },
					responseType: 'blob',
				},
			)

			const url = window.URL.createObjectURL(new Blob([res.data]))
			// const url = window.URL.createObjectURL(
			// 	new Blob([res.data], {
			// 		type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
			// 	}),
			// )

			const link = document.createElement('a')
			link.href = url
			link.setAttribute('download', 'PerfectCut.xlsx')
			document.body.appendChild(link)
			link.click()
			document.body.removeChild(link)
			window.URL.revokeObjectURL(url) // Clean up
		} catch (error) {
			message.error(error.response?.data?.message || error.message)
		} finally {
			setExcelBtnLoading(false)
		}
	}
	const handleExportTxt = async (selectedData) => {
		if (!selectedData?.length) {
			message.warning('Please select some data to export.')
			return
		}

		setTxtBtnLoading(true)

		const docEntries = selectedData.map((item) => item.docEntry).join(',')

		const body = {
			docEntries,
			startDate,
			endDate,
		}

		try {
			const res = await http.post(
				`api/perfectcut/export-to-txt-document`,
				body,
				{
					// headers: {
					// 	'Content-Type': 'text/plain',
					// },
					responseType: 'blob',
				},
			)

			const url = window.URL.createObjectURL(new Blob([res.data]))
			// const url = window.URL.createObjectURL(
			// 	new Blob([res.data], { type: 'text/plain' }),
			// )

			const link = document.createElement('a')
			link.href = url
			link.setAttribute('download', 'PerfectCut.txt')
			document.body.appendChild(link)
			link.click()
			document.body.removeChild(link)
			window.URL.revokeObjectURL(url)
		} catch (error) {
			message.error(error.response?.data?.message || error.message)
		} finally {
			setTxtBtnLoading(false)
		}
	}

	const handlePaginationChange = (page, pageSize) => {
		changePagination({
			page,
			pageSize,
			setCurrentPage,
			setPageSize,
		})
	}
	const handlePageSizeChange = (current, pageSize) => {
		changePageSize({
			current,
			pageSize,
			setCurrentPage,
			setPageSize,
		})
	}
	return (
		<Layout>
			<div className="m-5 border-[2px] border-gray-200">
				<h1 className="font-poppins ml-4 mt-10 text-xl font-bold text-black sm:ml-10 sm:mt-14 sm:text-2xl">
					{t('exportPerfectCut')}
				</h1>

				<div className="mt-10 w-full border-[1px] border-[#E8E8E8] sm:mt-14" />

				<div className="flex items-end justify-between mt-5 mx-10">
					<div className="flex items-center gap-4">
						<div className="flex flex-col gap-2">
							<p className="font-bold text-sm">
								{t('startDate')}
							</p>
							<Input
								type="date"
								value={startDate}
								className="w-[150px]"
								onChange={handleStartDate}
								max={endDate}
								allowClear
							/>
						</div>
						<div className="flex flex-col gap-2">
							<p className="font-bold text-sm">{t('endDate')}</p>
							<Input
								type="date"
								value={endDate}
								className="w-[150px]"
								onChange={handleEndDate}
								min={startDate}
								max={formattedDate}
								allowClear
							/>
						</div>
						<div className="flex flex-col gap-2">
							<p className="font-bold text-sm">{t('status')}</p>
							<Select
								options={[
									{ label: 'Barchasi', value: '' },
									{
										label: 'Ishlab chiqarish tugagan',
										value: 'Y',
									},
									{
										label: 'Ishlab chiqarish tugamagan',
										value: 'N',
									},
								]}
								value={absoluteStatus}
								className="w-[250px]"
								onChange={(value) => {
									setAbsoluteStatus(value)
								}}
								allowClear
							/>
						</div>
					</div>
					<div className="flex gap-2">
						<Button
							type="primary"
							className="h-8 "
							onClick={() => {
								handleExportTxt(selectedRows)
							}}
							disabled={selectedRowKeys.length === 0}
							loading={txtBtnLoading}
						>
							{t('exportTxtFile')}
						</Button>

						<Button
							type="primary"
							className="h-8 bg-red-600 hover:bg-red-400 disabled:bg-gray-100"
							onClick={() => {
								handleExportExcel(selectedRows)
							}}
							loading={excelBtnLoading}
							disabled={selectedRowKeys.length === 0}
						>
							<div className="flex items-center gap-2">
								{/* <FileDown /> */}
								{t('exportExcelFile')}
							</div>
						</Button>
					</div>
				</div>
				<div className="flex mt-10 items-start justify-between mx-10">
					<div className="flex flex-col flex-wrap gap-2 items-start">
						<span className="font-bold">
							{t('selectedDocNums')}
						</span>
						<span>
							{selectedRowDocNums.map(
								(docNum, index) =>
									`${index !== 0 ? ', ' : ''} ${docNum} `,
							)}
						</span>
					</div>
				</div>

				<div className="flex justify-end mr-10 mt-5 mb-5">
					<Pagination
						showTotal={(total) =>
							t('totalPaginationItems', { total })
						}
						showSizeChanger={true}
						current={currentPage + 1}
						onShowSizeChange={(pageSize, current) => {
							handlePageSizeChange(current, pageSize)
						}}
						total={data[0]?.documentsCount || 0} // ✅ correct
						pageSize={data[0]?.pageSize || 10}
						onChange={(page, pageSize) => {
							handlePaginationChange(page, pageSize)
						}}
					/>
				</div>

				<div className="mx-5 mb-5">
					<Table
						rowSelection={{
							selectedRowKeys,
							onChange: onSelectChange,
						}}
						columns={columns}
						dataSource={data}
						pagination={false}
						className="mt-6 w-full sm:mt-10 "
						rowKey={(row) => row.docEntry}
						bordered
						scroll={{ x: 900 }}
						loading={isLoading}
					/>
				</div>
			</div>

			<ViewProductionWorkersModal
				visible={isModalVisible}
				data={selectedRowData}
				onClose={() => {
					setIsModalVisible(false)
				}}
			/>
			<SuccessModalWithTimer
				getRef={(r) => {
					successRef.current = r
				}}
			/>
			<ErrorModalWithTimer
				getRef={(r) => {
					errorRef.current = r
				}}
			/>
		</Layout>
	)
}

export default ExportPerfectCut
